'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import * as React from 'react';
import { useControlledLatch } from '../../../lib/controlled-state';
import { developmentDiagnosticsEnabled } from '../../../lib/diagnostics';
import { createRenderIdentityPlan } from '../../../lib/render-identities';
import { cn } from '../../../lib/utils';
import { SpecraButton } from '../ensigns/button';
/** Underlined page-section navigation used by documents and touchpoints. */ function SpecraTabBarInner({ className, defaultValue, idBase, label = 'Sections', onChange, onValueChange, options, value, ...props }, forwardedRef) {
    const [internalValue, setInternalValue] = React.useState(defaultValue ?? options.find((option)=>!option.disabled)?.value);
    const valueControlled = useControlledLatch(value !== undefined);
    const selectedValue = valueControlled ? value : internalValue;
    const generatedId = React.useId();
    const tabRefs = React.useRef(new Map());
    const enabledOptions = options.filter((option)=>!option.disabled);
    const firstEnabledValue = enabledOptions[0]?.value;
    const rovingValue = enabledOptions.some((option)=>option.value === selectedValue) ? selectedValue : firstEnabledValue;
    const optionIdentityPlan = React.useMemo(()=>createRenderIdentityPlan(options.map((option)=>option.value)), [
        options
    ]);
    const duplicateOptionSignature = [
        ...optionIdentityPlan.duplicateIds
    ].sort().join(', ');
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && duplicateOptionSignature) {
            console.error(`SpecraTabBar option values must be unique. Duplicate values: ${duplicateOptionSignature}.`);
        }
    }, [
        duplicateOptionSignature
    ]);
    React.useEffect(()=>{
        if (!valueControlled && internalValue === undefined && firstEnabledValue !== undefined) {
            setInternalValue(firstEnabledValue);
        }
    }, [
        firstEnabledValue,
        internalValue,
        valueControlled
    ]);
    const select = (nextValue)=>{
        if (options.find((option)=>option.value === nextValue)?.disabled) return;
        if (!valueControlled) setInternalValue(nextValue);
        onValueChange?.(nextValue);
        onChange?.(nextValue);
    };
    const moveFocus = (currentValue, direction)=>{
        if (enabledOptions.length === 0) return;
        const currentIndex = enabledOptions.findIndex((option)=>option.value === currentValue);
        const nextIndex = currentIndex < 0 ? direction === 1 ? 0 : enabledOptions.length - 1 : (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
        const next = enabledOptions[nextIndex];
        if (next) {
            select(next.value);
            tabRefs.current.get(next.value)?.focus();
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        "aria-label": label,
        className: cn('flex max-w-full gap-[var(--space-card)] overflow-x-auto border-b border-border [scrollbar-width:thin]', className),
        ref: forwardedRef,
        role: "tablist",
        children: options.map((option, optionIndex)=>{
            const selected = option.value === selectedValue;
            const encodedValue = encodeURIComponent(option.value);
            const tabId = option.tabId ?? `${idBase ?? generatedId}-tab-${encodedValue}`;
            const panelId = option.panelId ?? (idBase ? `${idBase}-panel-${encodedValue}` : undefined);
            return /*#__PURE__*/ _createElement(SpecraButton, {
                ...option.buttonProps,
                ref: (node)=>{
                    if (node) tabRefs.current.set(option.value, node);
                    else tabRefs.current.delete(option.value);
                },
                "aria-controls": panelId,
                "aria-disabled": option.disabled || undefined,
                "aria-selected": selected,
                "data-tab-value": option.value,
                "data-testid": option.testId,
                className: cn('h-[var(--control-height-tab)] shrink-0 rounded-none border-b-2 border-x-0 border-t-0 px-0 text-[length:var(--fs-ui-caption)]', selected ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground', option.disabled && 'cursor-not-allowed opacity-50', option.buttonProps?.className),
                id: tabId,
                key: optionIdentityPlan.identities[optionIndex],
                onKeyDown: (event)=>{
                    option.buttonProps?.onKeyDown?.(event);
                    if (event.defaultPrevented) return;
                    const tabList = event.currentTarget.closest('[role="tablist"]');
                    const rtl = tabList?.dir === 'rtl' || Boolean(tabList?.closest('[dir="rtl"]')) || (tabList ? getComputedStyle(tabList).direction === 'rtl' : false);
                    if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        moveFocus(option.value, rtl ? -1 : 1);
                    } else if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        moveFocus(option.value, rtl ? 1 : -1);
                    } else if (event.key === 'Home') {
                        event.preventDefault();
                        const first = enabledOptions[0];
                        if (first) {
                            select(first.value);
                            tabRefs.current.get(first.value)?.focus();
                        }
                    } else if (event.key === 'End') {
                        event.preventDefault();
                        const last = enabledOptions.at(-1);
                        if (last) {
                            select(last.value);
                            tabRefs.current.get(last.value)?.focus();
                        }
                    }
                },
                onClick: ()=>{
                    if (!option.disabled) select(option.value);
                },
                role: "tab",
                tabIndex: option.value === rovingValue ? 0 : -1,
                variant: "ghost",
                children: [
                    /*#__PURE__*/ _jsx(React.Fragment, {
                        children: option.label
                    }, "label"),
                    option.count !== undefined ? /*#__PURE__*/ _jsx("span", {
                        className: "rounded-[var(--radius-sm)] bg-muted px-[var(--space-1-5)] py-[var(--space-0-5)] text-[length:var(--fs-ui-micro)]",
                        children: option.count
                    }, "count") : null
                ]
            });
        })
    });
}
const SpecraTabBarRoot = /*#__PURE__*/ React.forwardRef(SpecraTabBarInner);
SpecraTabBarRoot.displayName = 'SpecraTabBar';
export const SpecraTabBar = SpecraTabBarRoot;
/** Accessible panel counterpart for `SpecraTabBar` options. */ export const SpecraTabPanel = /*#__PURE__*/ React.forwardRef(({ active, children, className, id, idBase, keepMounted = false, tabId, tabIndex, value, ...props }, ref)=>{
    const hasAssociation = Boolean(tabId || idBase && value);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && !hasAssociation) {
            console.warn('SpecraTabPanel requires tabId or the idBase/value pair to produce a valid aria-labelledby relationship.');
        }
    }, [
        hasAssociation
    ]);
    if (!active && !keepMounted) return null;
    const encodedValue = value ? encodeURIComponent(value) : undefined;
    const resolvedTabId = tabId ?? (idBase && encodedValue ? `${idBase}-tab-${encodedValue}` : undefined);
    const resolvedPanelId = id ?? (idBase && encodedValue ? `${idBase}-panel-${encodedValue}` : undefined);
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        "aria-labelledby": resolvedTabId,
        className: cn('[&[hidden]]:hidden', className),
        hidden: !active,
        id: resolvedPanelId,
        ref: ref,
        role: "tabpanel",
        tabIndex: active ? tabIndex : undefined,
        children: children
    });
});
SpecraTabPanel.displayName = 'SpecraTabPanel';
